import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, ShieldCheck, Snowflake, RefreshCw } from "lucide-react";

const AdminMembers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "member" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/members");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/members", form);
      toast.success("تم إنشاء العضو");
      setForm({ name: "", email: "", password: "", role: "member" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleStatus = async (m) => {
    try {
      await api.patch(`/admin/members/${m.id}`, { status: m.status === "active" ? "frozen" : "active" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (m) => {
    if (!window.confirm(`حذف ${m.name}؟`)) return;
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-members">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline mb-3">الأعضاء</div>
          <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone">إدارة الأعضاء</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={load} className="btn-ghost" data-testid="members-refresh"><RefreshCw size={14} /></button>
          <button onClick={() => setShowAdd((v) => !v)} className="btn-gold" data-testid="members-add-toggle"><Plus size={14} />إضافة عضو</button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={onAdd} className="border border-gold/30 bg-ink-soft p-6 mb-8 grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="members-add-form">
          <input className="editorial-input" placeholder="الاسم" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="members-add-name" />
          <input className="editorial-input" placeholder="البريد" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} dir="ltr" data-testid="members-add-email" />
          <input className="editorial-input" placeholder="كلمة المرور (٨+)" type="password" required minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} dir="ltr" data-testid="members-add-password" />
          <div className="flex gap-3">
            <select className="editorial-input flex-1" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="member">عضو</option>
              <option value="admin">مدير</option>
            </select>
            <button type="submit" className="btn-gold" data-testid="members-add-submit">حفظ</button>
          </div>
        </form>
      )}

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-soft text-white/50 uppercase tracking-[0.2em] text-xs">
            <tr>
              <th className="px-4 py-3 text-right font-normal">الاسم</th>
              <th className="px-4 py-3 text-right font-normal">البريد</th>
              <th className="px-4 py-3 text-right font-normal">الدور</th>
              <th className="px-4 py-3 text-right font-normal">الحالة</th>
              <th className="px-4 py-3 text-right font-normal">التسجيل</th>
              <th className="px-4 py-3 text-right font-normal">إجراء</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {loading && <tr><td colSpan={6} className="p-6 text-center text-white/40">…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-white/40">لا يوجد أعضاء</td></tr>}
            {items.map((m) => (
              <tr key={m.id} className="hover:bg-white/5" data-testid={`member-row-${m.id}`}>
                <td className="px-4 py-3 font-arabic">{m.name}</td>
                <td className="px-4 py-3" dir="ltr">{m.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs uppercase tracking-[0.2em] ${m.role === "admin" ? "text-gold" : "text-white/50"}`}>{m.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs uppercase tracking-[0.2em] ${m.status === "frozen" ? "text-red-400" : "text-emerald-400"}`}>{m.status === "frozen" ? "مجمَّد" : "نشط"}</span>
                </td>
                <td className="px-4 py-3 text-white/45 text-xs" dir="ltr">{(m.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button onClick={() => toggleStatus(m)} title="تجميد/إلغاء التجميد" className="text-white/60 hover:text-gold" data-testid={`member-toggle-${m.id}`}><Snowflake size={14} /></button>
                    <button onClick={() => remove(m)} title="حذف" className="text-white/60 hover:text-red-400" data-testid={`member-delete-${m.id}`}><Trash2 size={14} /></button>
                    {m.role === "admin" && <ShieldCheck size={14} className="text-gold/60" />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminMembers;
